'use client';

import { useEffect, useRef, useState } from 'react';

import { FilterGroup } from '@/components/FilterGroup';
import {
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CATEGORY_FACET_KEY,
    LW_CATEGORY_FACET_KEY,
    LW_SHOW_OUT_OF_STOCK_FACET_KEY,
} from '@/components/SearchResultsFacets/constants';
import { StyledFieldset } from '@/components/SearchResultsFacets/styles';
import { ITEM_PROGRAM_FACET_KEY } from '@/constants/index';
import type { SearchServiceProps } from '@/services/search/types';
import { GRS_PROGRAM_TYPES_FACET_KEY } from '@/src/components/SearchResultsFacets/grs.constants';
import {
    BreadcrumbNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import { GRSFacetConfigProperties } from '@/types/contentStack';
import { PageType } from '@/types/pages/search';
import { Accordion } from '@nxt-costco-com/forge-components';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';

export function FacetsAccordion({
    categoryUrlPath,
    facet,
    facetConfig,
    grsFacetConfig,
    locale,
    pageType,
    categoryId,
    breadcrumb,
    searchService,
    publishedCategories,
}: {
    categoryUrlPath: string | undefined;
    facet: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    grsFacetConfig?: ContentStackEntryDataProps<GRSFacetConfigProperties>;
    locale: LocaleProps;
    pageType: PageType;
    breadcrumb?: BreadcrumbNormalized;
    searchService: SearchServiceProps;
    categoryId?: string;
    publishedCategories: string[] | undefined;
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    const accordionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const accordionInnerContent = accordionRef.current?.querySelector(
            "div.MuiAccordion-region[role='region']",
        );

        if (accordionInnerContent) {
            accordionInnerContent.removeAttribute('role');
        }
    }, []);

    function renderHeading(facet: FacetNormalized) {
        // TODO: Revisit this logic to check whether we have all the necessary facet keys covered
        if (
            facet.facetKey === ITEM_PROGRAM_FACET_KEY ||
            facet.facetKey === GRS_PROGRAM_TYPES_FACET_KEY
        ) {
            return ' ';
        }

        if (
            facet.facetKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY ||
            facet.facetKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY
        ) {
            return ' ';
        }
        return facet.label;
    }

    return (
        <Accordion
            title={renderHeading(facet)}
            // @ts-expect-error Accordion doesn't export this type
            variant="primary"
            isExpanded={isExpanded}
            onChange={() => setIsExpanded(!isExpanded)}
            uniqueId={facet.facetKey}
            ref={accordionRef}
        >
            {facet.facetKey === LW_CATEGORY_FACET_KEY ||
            facet.facetKey === GRS_CATEGORY_FACET_KEY ? (
                <FilterGroup
                    categoryUrlPath={categoryUrlPath}
                    facet={facet}
                    facetConfig={facetConfig}
                    grsFacetConfig={grsFacetConfig}
                    locale={locale}
                    pageType={pageType}
                    categoryId={categoryId}
                    breadcrumb={breadcrumb}
                    searchService={searchService}
                    publishedCategories={publishedCategories}
                />
            ) : (
                <StyledFieldset aria-labelledby={facet.facetKey}>
                    <FilterGroup
                        facet={facet}
                        facetConfig={facetConfig}
                        grsFacetConfig={grsFacetConfig}
                        locale={locale}
                        pageType={pageType}
                        searchService={searchService}
                        categoryId={categoryId}
                    />
                </StyledFieldset>
            )}
        </Accordion>
    );
}
